/**
 * The wire shapes of `Rws.AiInsights.Host.Administration.ProviderAllocationEndpoints` (issue #27,
 * CON-27 to CON-33): a person's total allowance, per-provider capability and setting, and the
 * whole-USD allocation an administrator saves. Gated on `entitlement.administer`.
 */
import { adminRequest, type AdminResult } from "./adminApi";
import { isProviderAllocationRequest } from "./requestTypes";

/** One provider's capability and the person's current setting for it. */
export interface ProviderAllocationState {
  vendorId: string;
  /** The name shown to a person, from the vendor catalogue. */
  displayName: string;
  supportsLimit: boolean;
  seatFeeUsd: number;
  enabled: boolean;
  /** The total allocated while the provider is on, seat fee included; zero when it is off. */
  amountUsd: number;
  /** Cost this month, seat fee included, for a provider switched on at any point this month; it becomes committed cost if switched off. */
  incurredCostUsd: number;
  /** The least that can be allocated while the provider is on: its seat fee, or the metered usage this month if that is more. */
  minimumAmountUsd: number;
  /** Whether the provider is granted per vendor access group (AIA-109); when true, at least one group is needed to enable it. */
  supportsAccessGroups?: boolean;
  /** The person's current access groups, when the provider supports them. */
  accessGroups?: string[] | null;
}

/** Everything the allocation screen needs for one person (`ProviderAllocationOverview`). */
export interface ProviderAllocationOverview {
  totalUsd: number;
  committedCostUsd: number;
  allocableUsd: number;
  hasAllocation: boolean;
  providers: ProviderAllocationState[];
}

/** One provider setting in a save request. */
export interface ProviderAllocationItem {
  vendorId: string;
  enabled: boolean;
  amountUsd: number;
  /** The access groups asked for; sent only for an enabled provider that supports them. */
  accessGroups?: string[];
}

/** The result of a successful save. */
export interface SetProviderAllocationResult {
  allocableUsd: number;
}

/** Calls `GET /api/users/{personId}/provider-allocation`. */
export async function fetchProviderAllocation(personId: string): Promise<AdminResult<ProviderAllocationOverview>> {
  return adminRequest<ProviderAllocationOverview>(
    `/api/users/${encodeURIComponent(personId)}/provider-allocation`,
    { method: "GET" },
    "provider allocation",
  );
}

/** Calls `PUT /api/users/{personId}/provider-allocation`. A 400 carries the server's reasons in `message`. */
export async function saveProviderAllocation(
  personId: string,
  providers: ProviderAllocationItem[],
): Promise<AdminResult<SetProviderAllocationResult>> {
  return adminRequest<SetProviderAllocationResult>(
    `/api/users/${encodeURIComponent(personId)}/provider-allocation`,
    { method: "PUT", body: JSON.stringify({ providers }) },
    "provider allocation",
  );
}

/** The result of raising an allocation-change request. */
export interface RaiseAllocationRequestResult {
  requestId: string;
  status: string;
}

/** The caller's own pending allocation-change request, with the settings it asks for. */
export interface PendingAllocationRequest {
  id: string;
  createdAt: string;
  providers: ProviderAllocationItem[];
}

interface MyRequestSummary {
  id: string;
  requestType: string;
  payloadJson: string;
  status: string;
  createdAt: string;
}

interface RequestedProviderPayload {
  vendorId?: string;
  enabled?: boolean;
  amountUsd?: number;
  VendorId?: string;
  Enabled?: boolean;
  AmountUsd?: number;
  accessGroups?: string[] | null;
  AccessGroups?: string[] | null;
}

const requestsPath = "/api/entitlement/requests";

/** Calls `GET /api/entitlement/provider-allocation/mine` (any signed-in, eligible person). */
export async function fetchMyProviderAllocation(): Promise<AdminResult<ProviderAllocationOverview>> {
  return adminRequest<ProviderAllocationOverview>("/api/entitlement/provider-allocation/mine", { method: "GET" }, "provider allocation");
}

/** Calls `POST /api/entitlement/requests/mine/provider-allocation`: raises a pending change for an approver to review. */
export async function requestMyProviderAllocation(providers: ProviderAllocationItem[]): Promise<AdminResult<RaiseAllocationRequestResult>> {
  return adminRequest<RaiseAllocationRequestResult>(
    `${requestsPath}/mine/provider-allocation`,
    { method: "POST", body: JSON.stringify({ providers }) },
    "allocation request",
  );
}

/** Calls `GET /api/entitlement/requests/mine` and returns the newest still-pending allocation change, if any. */
export async function fetchMyPendingAllocationRequest(): Promise<AdminResult<PendingAllocationRequest | null>> {
  const result = await adminRequest<MyRequestSummary[]>(`${requestsPath}/mine`, { method: "GET" }, "my requests");
  if (result.kind !== "ok") {
    return result;
  }

  const pending = result.response.find((request) => isProviderAllocationRequest(request.requestType) && request.status === "Pending");
  if (!pending) {
    return { kind: "ok", response: null };
  }

  return { kind: "ok", response: { id: pending.id, createdAt: pending.createdAt, providers: parseRequestedProviders(pending.payloadJson) } };
}

function parseRequestedProviders(payloadJson: string): ProviderAllocationItem[] {
  try {
    const payload = JSON.parse(payloadJson) as { providers?: RequestedProviderPayload[]; Providers?: RequestedProviderPayload[] };
    return (payload.providers ?? payload.Providers ?? []).map((item) => ({
      vendorId: item.vendorId ?? item.VendorId ?? "",
      enabled: item.enabled ?? item.Enabled ?? false,
      amountUsd: item.amountUsd ?? item.AmountUsd ?? 0,
      accessGroups: item.accessGroups ?? item.AccessGroups ?? [],
    }));
  } catch {
    return [];
  }
}
/** Calls `POST /api/entitlement/requests/{id}/withdraw` for the caller's own pending request. */
export async function withdrawMyRequest(id: string): Promise<AdminResult<null>> {
  return adminRequest<null>(`${requestsPath}/${encodeURIComponent(id)}/withdraw`, { method: "POST" }, "withdraw request");
}
